"use client";

import { Upload, FileText, X, FolderOpen, Trash, Download } from "lucide-react";
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import type React from "react";

import { Button } from "@/app/_module/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/app/_module/components/ui/card";
import { useToast } from "@/app/_module/hooks/use-toast";
import { resendSMSForAction } from "@/app/_module/services/auth";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";

import { useUploadLeadDocuments, useLeadDocuments, useDeleteLeadDocument, useInitiateLeadDocumentDownload, useVerifyLeadDocumentDownload, useGetDocumentDownloadUrl } from "../hooks/queries";
import type { DownloadInitiateResponse } from "../types/initiate-lead-document-dowload";
import type { LeadDocument } from "../types/lead-document-upload";

import ConfirmDocumentDeleteModal from "./ConfirmDocumentDeleteModal";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface LeadDocumentWithFile {
    id: string;
    fileName: string;
    fileType: string;
    file: File;
}

interface LeadDocumentsProps {
    leadId?: string;
    initialFiles?: Omit<LeadDocument, "file">[];
    allowMultiple?: boolean;
    accept?: string;
}

export const LeadDocuments: React.FC<LeadDocumentsProps> = function ({
    leadId,
    allowMultiple = true,
    accept = ".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg",
}) {
    const { toast } = useToast();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const uploadDocs = useUploadLeadDocuments();
    const downloadMutation = useInitiateLeadDocumentDownload(leadId ?? "");
    const verifyMutation = useVerifyLeadDocumentDownload(leadId ?? "");
    const downloadUrlMutation = useGetDocumentDownloadUrl();

    // Fetch documents from API
    const { data: documents, isLoading, isError, refetch } = useLeadDocuments(
        leadId ?? ""
    );

    // Uploaded files from API
    const [uploadedFiles, setUploadedFiles] = useState<LeadDocument[]>([]);
    const [selectedFiles, setSelectedFiles] = useState<LeadDocumentWithFile[]>([]);
    const [isPicking, setIsPicking] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState<LeadDocument | null>(null);
    const [showMFAModal, setShowMFAModal] = useState(false);
    const [pendingDownload, setPendingDownload] = useState<{ doc: LeadDocument; downloadData: DownloadInitiateResponse["data"]["data"]; downloadToken?: string } | null>(null);
    // Use a ref to store the download token immediately (avoids React async state update timing issues)
    const downloadTokenRef = useRef<string | null>(null);

    // Populate uploaded files once API data is loaded
    useEffect(() => {
        if (documents && Array.isArray(documents)) {
            setUploadedFiles(documents);
        } else if (documents && !Array.isArray(documents)) {
            // Handle nested response format
            const docsArray = (documents as any)?.data || [];
            if (Array.isArray(docsArray) && docsArray.length > 0) {
                setUploadedFiles(docsArray);
            }
        }
    }, [documents]); // React Query handles caching, safe to use object reference

    const hasAnyFiles = uploadedFiles.length > 0 || selectedFiles.length > 0;

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const fileSize = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        return `${fileSize} ${sizes[i]}`;
    };

    const handleOpenPicker = () => {
        if (inputRef.current) {
            setIsPicking(true);
            inputRef.current.click();

            // Reset isPicking when the file dialog closes (whether files were selected or not)
            // This handles the cancel case
            const resetPicking = () => {
                setTimeout(() => {
                    setIsPicking(false);
                }, 100);
                inputRef.current?.removeEventListener('cancel', resetPicking);
                window.removeEventListener('focus', resetPicking);
            };

            // Listen for cancel event (when user clicks cancel in file dialog)
            inputRef.current.addEventListener('cancel', resetPicking, { once: true });
            // Also listen for window focus (backup in case cancel event doesn't fire)
            window.addEventListener('focus', resetPicking, { once: true });
        }
    };

    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB per file
    const MAX_FILES_PER_UPLOAD = 20; // Maximum 20 files per upload
    const MAX_TOTAL_FILES = 100; // Maximum 100 total files per lead

    // Shared file-intake logic used by BOTH the click-to-select input and the
    // drag-and-drop handler — same validation (count/size/limit) either way.
    const addFiles = (files: FileList | File[]) => {
        const fileArr = Array.from(files);
        if (fileArr.length === 0) {
            setIsPicking(false);
            return;
        }

        // Security: Check if total files would exceed limit
        const currentTotal = uploadedFiles.length + selectedFiles.length;
        if (currentTotal >= MAX_TOTAL_FILES) {
            setUploadError(`Maximum ${MAX_TOTAL_FILES} files allowed per lead. Please delete some files before uploading more.`);
            setIsPicking(false);
            return;
        }

        // Security: Limit files per upload to prevent spam
        const filesToAdd = fileArr.slice(0, MAX_FILES_PER_UPLOAD);
        const excessFiles = fileArr.length - filesToAdd.length;

        const added: LeadDocumentWithFile[] = filesToAdd.map((file, index) => ({
            id: `${Date.now()}-${index}`,
            fileName: file.name,
            fileType: file.type || "application/octet-stream",
            file,
        }));

        // Filter by size and check against total limit
        const valid = added.filter((f) => f.file.size <= MAX_FILE_SIZE_BYTES);
        const remainingSlots = MAX_TOTAL_FILES - currentTotal;
        const finalValid = valid.slice(0, remainingSlots);

        const rejectedBySize = added.length - valid.length;
        const rejectedByLimit = valid.length - finalValid.length;

        if (excessFiles > 0 || rejectedBySize > 0 || rejectedByLimit > 0) {
            const errors = [];
            if (excessFiles > 0) {
                errors.push(`Only ${MAX_FILES_PER_UPLOAD} files can be uploaded at once. ${excessFiles} file(s) were excluded.`);
            }
            if (rejectedBySize > 0) {
                errors.push(`${rejectedBySize} file(s) exceed the 10MB size limit.`);
            }
            if (rejectedByLimit > 0) {
                errors.push(`${rejectedByLimit} file(s) would exceed the ${MAX_TOTAL_FILES} total files limit.`);
            }
            setUploadError(errors.join(" "));
        } else {
            setUploadError(null);
        }

        setSelectedFiles((prev) => [...prev, ...finalValid]);
        setIsPicking(false);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(e.target.files || []);
        e.currentTarget.value = "";
    };

    // Drag-and-drop onto the upload area. preventDefault on dragover is required
    // for the browser to fire a drop event (instead of just opening the file).
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };
    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        // Only clear when actually leaving the dropzone (not entering a child).
        if (e.currentTarget === e.target) setIsDragging(false);
    };
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer?.files;
        if (dropped && dropped.length > 0) addFiles(dropped);
    };

    const removeSelected = (id: string) => {
        setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleUpload = async () => {
        // DEMO SANDBOX: an upload writes a REAL file to Supabase storage that the seat reset does not clear —
        // so a stranger's document would outlive their session and greet the next visitor.
        // The sample document on this lead shows what the feature does.
        if (isDemoMode()) {
          window.dispatchEvent(
            new CustomEvent('demo-restricted', {
              detail:
                "Uploading documents is disabled in the demo sandbox. Start a free trial to store your real client files.",
            }),
          );
          return;
        }

        if (!leadId || selectedFiles.length === 0) {
            return;
        }

        try {
            const files = selectedFiles.map((f) => f.file);
            const result = await uploadDocs.mutateAsync({ leadId, files });
            // Refresh list after upload
            const refetchResult = await refetch();
            // Clear selected files after successful upload
            setSelectedFiles([]);
            setUploadError(null);
            toast({
                title: "Upload Successful",
                description: `${files.length} file(s) uploaded successfully.`,
            });
        } catch (error) {
            console.error("Failed to upload documents:", error);
            const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.';
            setUploadError(errorMessage);
            toast({
                title: "Upload Failed",
                description: errorMessage,
                variant: "destructive",
            });
        }
    };

    const deleteDocMutation = useDeleteLeadDocument(leadId ?? "");

    const handleDeleteClick = (doc: LeadDocument) => {
        // DEMO SANDBOX: the sample document is shared furniture — deleting it would leave the next visitor an
        // empty tab with nothing to look at.
        if (isDemoMode()) {
          window.dispatchEvent(
            new CustomEvent('demo-restricted', {
              detail:
                "Deleting documents is disabled in the demo sandbox. Start a free trial to manage your real client files.",
            }),
          );
          return;
        }

        setDocumentToDelete(doc);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!documentToDelete) return;

        try {
            await deleteDocMutation.mutateAsync(documentToDelete.id);
            setUploadedFiles((prev) =>
                prev.filter((f) => f.id !== documentToDelete.id)
            );
            setDocumentToDelete(null);
            toast({
                title: "Document Deleted",
                description: "The document has been removed successfully.",
            });
        } catch (error) {
            console.error("Failed to delete document:", error);
            toast({
                title: "Failed to Delete Document",
                description: error instanceof Error ? error.message : "An error occurred while deleting the document. Please try again.",
                variant: "destructive",
            });
            setDocumentToDelete(null);
        }
    };

    const handleDownloadClick = async (doc: LeadDocument) => {
        try {
            const result = await downloadMutation.mutateAsync(doc.id);

            // Backend returns { statusCode, message, data, timestamp }
            // The actual download data is in result.data
            const downloadData = (result as any)?.data || result;

            // Check if verification is required
            if (!downloadData.requiresVerification) {
                // User has no MFA - download directly without verification
                toast({
                    title: "No Verification Required",
                    description: "Generating download link...",
                });

                // Generate download token directly (backend will skip verification for non-MFA users)
                // We can pass any string as the verification code since backend checks hasMfaEnabled first
                const verifyResult = await verifyMutation.mutateAsync({
                    documentId: doc.id,
                    verificationCode: "bypass",  // Backend ignores this for non-MFA users
                });

                // Extract download token from nested response
                const downloadToken = (verifyResult as any)?.data?.data?.downloadToken ||
                                     (verifyResult as any)?.data?.downloadToken ||
                                     (verifyResult as any)?.downloadToken;

                if (downloadToken) {
                    const urlResult = await downloadUrlMutation.mutateAsync(downloadToken);

                    // Backend returns { downloadUrl, fileName, fileType, expiresAt }
                    const downloadUrl = (urlResult as any)?.downloadUrl ||
                                       (urlResult as any)?.data?.downloadUrl ||
                                       (urlResult as any)?.url;

                    if (downloadUrl) {
                        // Fetch the file as a blob to enable proper download (cross-origin URLs don't support download attribute)
                        const response = await fetch(downloadUrl);
                        const blob = await response.blob();

                        // Create a blob URL and trigger download
                        const blobUrl = window.URL.createObjectURL(blob);

                        // Download the file
                        const downloadLink = document.createElement('a');
                        downloadLink.href = blobUrl;
                        downloadLink.download = doc.fileName || 'document';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);

                        // Also open in new tab
                        window.open(blobUrl, '_blank');

                        // Clean up the blob URL after a delay to ensure both actions complete
                        setTimeout(() => {
                            window.URL.revokeObjectURL(blobUrl);
                        }, 1000);

                        toast({
                            title: "Download Started",
                            description: "Your document has been downloaded and opened.",
                        });
                    } else {
                        toast({
                            title: "Download Failed",
                            description: "Could not retrieve download URL. Please try again.",
                            variant: "destructive",
                        });
                    }
                } else {
                    toast({
                        title: "Download Failed",
                        description: "Could not generate download token. Please try again.",
                        variant: "destructive",
                    });
                }

                return;
            }

            // User has MFA - show verification modal
            setPendingDownload({ doc, downloadData });
            setShowMFAModal(true);

        } catch (err) {
            toast({
                title: "Download Failed",
                description: "Failed to initiate download. Please try again.",
                variant: "destructive",
            });
        }
    };

    // MFA verification handler for SimplifiedMFAModal
    // This passes the actual OTP code to the document verify endpoint (which does its own MFA verification)
    const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
        if (!pendingDownload) return false;

        try {
            // Call document verify endpoint with the actual OTP code
            // The backend's verifyDownloadCode does its own MFA verification
            const verifyResult = await verifyMutation.mutateAsync({
                documentId: pendingDownload.downloadData.documentId,
                verificationCode: code,
                method: method,
            });

            // Extract download token - handleApiCall + extractApiData should unwrap double-wrapped responses
            // Result should be { downloadToken, expiresIn, documentInfo } directly
            const result = verifyResult as any;
            const downloadToken = result?.downloadToken ||
                                 result?.data?.downloadToken ||
                                 result?.data?.data?.downloadToken;

            if (!downloadToken) {
                return false;
            }

            // Store in ref immediately (sync) so handleMFAVerified can access it
            downloadTokenRef.current = downloadToken;
            return true;
        } catch (error) {
            console.error('[LeadDocuments] MFA verify error:', error);
            return false;
        }
    };

    // Called after MFA is successfully verified - proceed with actual download
    const handleMFAVerified = async () => {
        if (!pendingDownload) return;

        // Get the download token from the ref (set synchronously in handleMFAVerify)
        const downloadToken = downloadTokenRef.current;

        if (!downloadToken) {
            toast({
                title: "Download Failed",
                description: "No download token received. Please try again.",
                variant: "destructive",
            });
            return;
        }

        try {
            // Get the actual download URL
            const urlResult = await downloadUrlMutation.mutateAsync(downloadToken);

            // Extract download URL - may be nested in data property or named downloadUrl
            const downloadUrl = (urlResult as any)?.data?.downloadUrl ||
                               (urlResult as any)?.data?.url ||
                               (urlResult as any)?.downloadUrl ||
                               (urlResult as any)?.url;

            if (!downloadUrl) {
                toast({
                    title: "Download Failed",
                    description: "Could not generate download URL. Please try again.",
                    variant: "destructive",
                });
                return;
            }

            // Fetch file as blob and download
            const response = await fetch(downloadUrl);
            const blob = await response.blob();

            // Create blob URL and trigger download
            const blobUrl = window.URL.createObjectURL(blob);

            // Download the file
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = pendingDownload.doc.fileName || 'document';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            // Also open in new tab
            window.open(blobUrl, '_blank');

            // Clean up blob URL after a delay to ensure both actions complete
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 1000);

            toast({
                title: "Download Started",
                description: "Your document has been downloaded and opened.",
            });

            // Clear state
            setPendingDownload(null);
            downloadTokenRef.current = null;
        } catch (err: any) {
            toast({
                title: "Download Failed",
                description: err?.message || "Failed to download document. Please try again.",
                variant: "destructive",
            });
            // Clear ref on error too
            downloadTokenRef.current = null;
        }
    };

    const totalSelectedSize = useMemo(
        () => selectedFiles.reduce((acc, f) => acc + f.file.size, 0),
        [selectedFiles]
    );

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FolderOpen className="h-5 w-5" />
                        Lead Documents
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    {!!isLoading && <div className="text-sm text-gray-500">Loading documents...</div>}
                    {!!isError && <div className="text-sm text-red-600">Failed to load documents.</div>}

                    <div className="flex flex-col gap-4">
                        {/* Upload Area */}
                        <div
                            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                isDragging
                                    ? "border-primary bg-primary/5"
                                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                            }`}
                            onClick={handleOpenPicker}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenPicker();
                                }
                            }}
                        >
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className={`w-8 h-8 mb-4 ${isDragging ? "text-primary" : "text-gray-500"}`} />
                                <p className="mb-2 text-sm text-gray-600">
                                    {isDragging ? (
                                        <span className="font-semibold text-primary">Drop files to upload</span>
                                    ) : (
                                        <>
                                            <span className="font-semibold">Click to select</span> or drag and drop
                                        </>
                                    )}
                                </p>
                                <p className="text-xs text-gray-500">
                                    {accept.replaceAll(",", ", ")} • Max 10MB per file
                                </p>
                            </div>
                            <input
                                ref={inputRef}
                                accept={accept}
                                className="hidden"
                                disabled={isPicking}
                                id="lead-docs-file-input"
                                multiple={allowMultiple}
                                onChange={handleFileInput}
                                type="file"
                            />
                        </div>

                        {!!uploadError && <div className="text-sm text-red-600">{uploadError}</div>}

                        {!hasAnyFiles && !isLoading && (
                            <div className="flex flex-col items-center justify-center py-8 text-center border rounded-lg bg-gray-50">
                                <FileText className="h-8 w-8 text-muted-foreground mb-3" />
                                <div className="text-sm text-muted-foreground">
                                    No documents uploaded for this lead yet.
                                </div>
                                <Button
                                    className="mt-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                                    onClick={handleOpenPicker}
                                >
                                    Upload Document
                                </Button>
                            </div>
                        )}

                        {/* Pending Selected */}
                        {selectedFiles.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">Pending Uploads</div>
                                    <div className="text-xs text-muted-foreground">
                                        {selectedFiles.length} file
                                        {selectedFiles.length !== 1 ? "s" : ""} •{" "}
                                        {formatFileSize(totalSelectedSize)}
                                    </div>
                                </div>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {selectedFiles.map((file) => (
                                        <div
                                            key={file.id}
                                            className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                                        >
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-primary" />
                                                <div>
                                                    <p className="text-sm font-medium">{file.fileName}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {file.fileType || "Unknown type"} •{" "}
                                                        {formatFileSize(file.file.size)}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                className="h-6 w-6 p-0"
                                                onClick={() => removeSelected(file.id)}
                                                size="sm"
                                                variant="ghost"
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                                        disabled={uploadDocs.isPending}
                                        onClick={handleUpload}
                                    >
                                        {uploadDocs.isPending ? "Uploading..." : "Upload Documents"}
                                    </Button>
                                    <Button onClick={() => setSelectedFiles([])} variant="outline">
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Uploaded List */}
                        {uploadedFiles.length > 0 && (
                            <div className="space-y-3">
                                <div className="text-sm font-medium">Uploaded Documents</div>
                                <div className="space-y-2 max-h-56 overflow-y-auto">
                                    {uploadedFiles.map((doc) => (
                                        <div
                                            key={doc.id}
                                            className="flex items-center justify-between p-2 bg-white rounded border"
                                        >
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-green-600" />
                                                <div>
                                                    <p className="text-sm font-medium">{doc.fileName}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {doc.fileType || "Unknown type"} •{" "}
                                                        {formatFileSize(parseInt(doc.fileSize))}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex flex-row-reverse gap-2">
                                                <Button
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => handleDeleteClick(doc)}
                                                    size="sm"
                                                    variant="ghost"
                                                >
                                                    <Trash className="h-3 w-3" color="red" />
                                                </Button>

                                                <Button
                                                    className="h-6 w-6 p-0"
                                                    onClick={() => handleDownloadClick(doc)}
                                                    size="sm"
                                                    variant="ghost"
                                                >
                                                    <Download className="h-3 w-3" color="blue" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Delete Modal */}
                    {!!documentToDelete && (
                        <ConfirmDocumentDeleteModal
                            fileName={documentToDelete.fileName}
                            onConfirm={handleConfirmDelete}
                            onOpenChange={setDeleteModalOpen}
                            open={deleteModalOpen}
                        />
                    )}

                    {/* MFA Verification Modal for Document Download */}
                    <SimplifiedMFAModal
                        isOpen={showMFAModal}
                        onClose={() => {
                            setShowMFAModal(false);
                            setPendingDownload(null);
                            downloadTokenRef.current = null;
                        }}
                        onVerified={handleMFAVerified}
                        onVerify={handleMFAVerify}
                        onSendSMS={resendSMSForAction}
                        title="Verify to Download Document"
                        description={`Please verify your identity with 2FA to download "${pendingDownload?.doc.fileName || 'this document'}".`}
                    />
                </CardContent>
            </Card>
        </div>
    );
};

export default LeadDocuments;
